/**
 * 3D Model Generation Tools
 *
 * `generate_model` builds a mesh from a text prompt (optionally guided by an
 * image already in the scene) and adds it to the scene.
 *
 * This is the *prompt-driven* path and it runs entirely server-side: the
 * gateway holds the provider key, bills the team, and decides which provider
 * answers — Rodin today, TRELLIS.2 on our own GPU stack later. Nothing here
 * names one, which is the point.
 *
 * It does not replace `generate_3d` (TRELLIS.2 via the inference host), which
 * converts an Image object that already exists. The split is by input: an
 * object to convert goes to generate_3d, a description goes here.
 */

import { editModeOnly } from './authoring-ops';
import { dispatchCatalogImport } from '../../catalog-import';
import { topLevelParent } from 'trace-viewer/editor';
import type { ToolDefinition } from './types';
import { readTransformArgs } from 'trace-viewer/editor';
import type { ToolContext } from '../agent-helpers';
import { applyTransform, groundAlign, resolveObject } from '../agent-helpers';
import { getImageBlob, isImageSceneObject, isVideoSceneObject } from '../../inference/shared';
import { persistGeneratedBinary } from './persist-generated';
import { importBlobIntoSceneFolder, registerFolderFileName } from '../../cms/import-into-folder';
import {
    generateModel3d,
    getGatewayRoute,
    hasGatewayCapability,
    isGatewayConfigured,
    type ModelGenRequest,
} from '../gateway';

/** What the tool timeline shows in place of a model id. */
function model3dLabel(): string {
    return getGatewayRoute('model3d')?.label ?? 'Trace AI · 3D';
}

/** Collect the optional reference image, or an error string. */
async function referenceImages(
    ctx: ToolContext,
    reference: string | undefined,
): Promise<ModelGenRequest['images'] | { error: string }> {
    if (!reference) return undefined;
    const source = resolveObject(reference, ctx);
    if (!source) return { error: `Reference object "${reference}" was not found in the scene.` };
    if (!isImageSceneObject(source) && !isVideoSceneObject(source)) {
        return { error: `Reference object "${source.name}" is not an Image or Video object.` };
    }
    try {
        // Video sources contribute their currently displayed frame.
        const blob = await getImageBlob(source, ctx.traceScene);
        return [blob.type ? blob : new Blob([blob], { type: 'image/png' })];
    } catch (err) {
        return { error: `Could not read reference image: ${err instanceof Error ? err.message : String(err)}` };
    }
}

async function executeGenerateModel(
    input: Record<string, unknown>,
    ctx: ToolContext,
): Promise<string> {
    const prompt = (input.prompt as string | undefined)?.trim();
    const reference = input.reference as string | undefined;
    const quality = input.quality as ModelGenRequest['quality'];
    const name = input.name as string | undefined;
    const transformArgs = readTransformArgs(input);
    if ('error' in transformArgs) return transformArgs.error;
    const { position, rotation, scale } = transformArgs;

    if (!prompt && !reference) {
        return 'Error: generate_model needs a prompt describing the model (a reference object alone is not enough).';
    }
    if (!hasGatewayCapability('model3d')) {
        return isGatewayConfigured()
            ? 'Error: 3D generation runs server-side and needs a Trace sign-in. Sign in to the catalog, or switch the AI source to Trace AI in settings.'
            : 'Error: 3D generation requires the Trace AI gateway, which this build has no URL for.';
    }

    const images = await referenceImages(ctx, reference);
    if (images && 'error' in images) return `Error: ${images.error}`;

    console.log(`[AI] generate_model: "${prompt?.slice(0, 100)}"${reference ? ` (reference: ${reference})` : ''}`);
    const result = await generateModel3d(
        { prompt, quality, ...(images ? { images } : {}) },
        {
            onProgress: message => {
                console.log(`[AI] generate_model: ${message}`);
                ctx.onToolProgress?.(message);
            },
        },
    );
    if ('error' in result) {
        console.error('[AI] generate_model error:', result.error);
        return `Error generating 3D model: ${result.error}`;
    }

    const { blob, elapsedMs } = result;
    const basis = prompt ?? reference ?? 'model';
    const sanitized = basis.slice(0, 40).replace(/[^a-zA-Z0-9_-]/g, '_').replace(/_+/g, '_');
    const fileName = `generated_${sanitized}.glb`;
    const objectName = name ?? (basis.slice(0, 30).replace(/[^a-zA-Z0-9 ]/g, '').trim() || 'GeneratedModel');
    const sizeMb = (blob.size / 1024 / 1024).toFixed(1);

    const viewer = ctx.viewer;
    if (!viewer?.import) {
        // Headless (bench/MCP): no <trace-viewer> to import through.
        try {
            const relSrc = await persistGeneratedBinary(ctx, blob, 'glb');
            const object = await ctx.traceScene.createSceneObject(
                objectName, topLevelParent(ctx.traceScene) ?? undefined, relSrc,
            );
            groundAlign(object.transform);
            applyTransform(object.transform, position, rotation, scale);
            ctx.traceScene.select(object);
            ctx.onSceneChanged?.();
            console.log(`[AI] generate_model (headless): created "${object.name}" -> ${relSrc} (${blob.size} bytes)`);
            return `Generated 3D model "${object.name}" and added it to the scene (${sizeMb} MB, ${Math.round(elapsedMs / 1000)}s).`;
        } catch (err) {
            return `Error adding generated model to scene (headless): ${err instanceof Error ? err.message : String(err)}`;
        }
    }

    try {
        // A CMS scene owns a folder; put the generated bytes in it now so the
        // object is authored with a folder-relative `src` (spec §2.6).
        const folderImport = await importBlobIntoSceneFolder(blob, fileName, ctx.traceScene);
        const { sceneObject } = await viewer.import(
            folderImport.file, folderImport.file.name, objectName,
        );
        // `files[k].name` carries the generated file's own name, not the
        // hash the folder stored it under (spec §2.1).
        registerFolderFileName(ctx.traceScene, folderImport);
        groundAlign(sceneObject.transform);
        applyTransform(sceneObject.transform, position, rotation, scale);

        // Same event the catalog import path fires, so undo records it.
        await dispatchCatalogImport(sceneObject);

        ctx.traceScene.select(sceneObject);
        ctx.onSceneChanged?.();

        console.log(`[AI] generate_model: created "${sceneObject.name}" (${blob.size} bytes, ${Math.round(elapsedMs / 1000)}s)`);
        return `Generated 3D model "${sceneObject.name}" and added it to the scene (${sizeMb} MB, ${Math.round(elapsedMs / 1000)}s). `
            + 'It is a full mesh sitting on the ground plane — position, scale and reparent it like any imported model.';
    } catch (err) {
        return `Error adding generated model to scene: ${err instanceof Error ? err.message : String(err)}`;
    }
}

export const model3dTools: ToolDefinition[] = [
    {
        tool: {
            name: 'generate_model',
            input_schema: {
                type: 'object',
                properties: {
                    prompt: { type: 'string' },
                    reference: { type: 'string' },
                    quality: {
                        type: 'string',
                        enum: ['draft', 'standard', 'high'],
                    },
                    name: { type: 'string' },
                    position: {
                        type: 'array', items: { type: 'number' }, minItems: 3, maxItems: 3,
                    },
                    rotation: {
                        type: 'array', items: { type: 'number' }, minItems: 3, maxItems: 3,
                    },
                    scale: {
                        type: 'array', items: { type: 'number' }, minItems: 3, maxItems: 3,
                    },
                },
                required: ['prompt'],
            },
        },
        execute: editModeOnly(executeGenerateModel),
        modelLabel: model3dLabel,
    },
];
